import { ref, onBeforeUnmount, computed } from 'vue';
import socketService, { WebSocketEvents } from '@/api/socketService';

export function useSocket() {
  const isConnected = ref(false);
  const connectionError = ref(null);

  // Initialize connection
  const connect = (url = null) => {
    try {
      socketService.connect(url);
      setupSocketListeners();
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      connectionError.value = error.message;
    }
  };

  // Setup basic socket event listeners
  const setupSocketListeners = () => {
    socketService.on('connect', () => {
      isConnected.value = true;
      connectionError.value = null;
    });

    socketService.on('disconnect', () => {
      isConnected.value = false;
    });

    socketService.on('connect_error', (error) => {
      connectionError.value = error.message;
      isConnected.value = false;
    });
  };

  // Join a specific room
  const joinRoom = (room) => {
    socketService.joinRoom(room);
  };

  // Leave a specific room
  const leaveRoom = (room = null) => {
    socketService.leaveRoom(room);
  };

  // Listen to custom events
  const on = (event, callback) => {
    socketService.on(event, callback);
  };

  // Remove event listeners
  const off = (event, callback = null) => {
    socketService.off(event, callback);
  };

  // Emit events
  const emit = (event, data) => {
    socketService.emit(event, data);
  };
  // Computed status
  const status = computed(() => socketService.getConnectionStatus());

  const disconnect = () => {
    socketService.disconnect();
  };

  // Cleanup on component unmount
  onBeforeUnmount(() => {
    disconnect();
  });

  return {
    // State
    isConnected,
    connectionError,
    status,

    // Methods
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    on,
    off,
    emit,
  };
}

// Specific composable for chat room functionality
export function useChatSocket(cid, options = { enabled: true }) {
  const socket = useSocket(cid);
  const messageHistory = ref([]);
  const isPageVisible = ref(!document.hidden);
  const lastHeartbeat = ref(Date.now());
  const healthCheckInterval = ref(null);
  const heartbeatInterval = ref(null);

  // Store original callback references for re-registration
  const eventCallbacks = ref({
    newChatMessages: null,
    notification: null,
    userStatus: null,
    streamingUpdate: null,
    error: null,
  });

  // Auto-connect when composable is used
  const initializeSocket = async () => {
    socket.connect();
    setupChatEventListeners();
    setupPageVisibilityListener();
    startHealthCheck();
    startHeartbeat();

    try {
      await waitForConnection();
      socket.joinRoom(cid);
    } catch (error) {
      console.warn('Connection timeout.');
    }
  };

  // Setup page visibility listener for mobile background handling
  const setupPageVisibilityListener = () => {
    const handleVisibilityChange = async () => {
      isPageVisible.value = !document.hidden;

      if (isPageVisible.value) {
        // Check if connection is healthy when page becomes visible
        const isHealthy = await checkConnectionHealth();

        if (!isHealthy) {
          await refreshSocket({
            clearHistory: false,
            reconnectDelay: 500,
            preserveRoom: true,
          });
        }
      } else {
        // Stop heartbeat when page is hidden to save resources
        stopHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });
  };

  // Check connection health
  const checkConnectionHealth = async () => {
    try {
      // Check if socket is connected
      if (!socket.isConnected.value) {
        return false;
      }

      // Check if heartbeat is recent (within last 30 seconds)
      const now = Date.now();
      const timeSinceLastHeartbeat = now - lastHeartbeat.value;
      if (timeSinceLastHeartbeat > 30000) {
        return false;
      }

      // Try to emit a ping and wait for response
      return await pingSocket();
    } catch (error) {
      console.error('Connection health check failed:', error);
      return false;
    }
  };

  // Ping socket to verify connection
  const pingSocket = () => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(false);
      }, 5000); // 5 second timeout

      // Listen for pong response
      const pongHandler = () => {
        clearTimeout(timeout);
        socket.off('pong', pongHandler);
        resolve(true);
      };

      socket.on('pong', pongHandler);
      socket.emit('ping');
    });
  };

  // Start health check interval
  const startHealthCheck = () => {
    if (healthCheckInterval.value) {
      clearInterval(healthCheckInterval.value);
    }

    healthCheckInterval.value = setInterval(async () => {
      if (isPageVisible.value) {
        const isHealthy = await checkConnectionHealth();
        if (!isHealthy) {
          await refreshSocket({
            clearHistory: false,
            reconnectDelay: 1000,
            preserveRoom: true,
          });
        }
      }
    }, 60000); // Check every minute
  };

  // Start heartbeat to maintain connection
  const startHeartbeat = () => {
    if (heartbeatInterval.value) {
      clearInterval(heartbeatInterval.value);
    }

    heartbeatInterval.value = setInterval(() => {
      if (socket.isConnected.value && isPageVisible.value) {
        socket.emit('ping');
        lastHeartbeat.value = Date.now();
      }
    }, 20000); // Send heartbeat every 20 seconds
  };

  // Stop heartbeat
  const stopHeartbeat = () => {
    if (heartbeatInterval.value) {
      clearInterval(heartbeatInterval.value);
      heartbeatInterval.value = null;
    }
  };

  // Stop health check
  const stopHealthCheck = () => {
    if (healthCheckInterval.value) {
      clearInterval(healthCheckInterval.value);
      healthCheckInterval.value = null;
    }
  };

  // Setup chat-specific event listeners
  const setupChatEventListeners = () => {
    // Listen for new chat messages specifically
    const newChatMessagesCallback = (data) => {
      if (data && data.messages) {
        messageHistory.value.push(...data.messages);
      }
    };

    eventCallbacks.value.newChatMessages = newChatMessagesCallback;
    socket.on(WebSocketEvents.NEW_CHAT_MESSAGES, newChatMessagesCallback);
  };

  // Refresh socket mechanism
  const refreshSocket = async (options = {}) => {
    const { clearHistory = false, reconnectDelay = 1000, preserveRoom = true } = options;

    try {
      // 1. Stop intervals
      if (healthCheckInterval.value) {
        clearInterval(healthCheckInterval.value);
      }
      stopHeartbeat();
      stopHealthCheck();

      // 2. Clean up existing listeners
      cleanupEventListeners();

      // 3. Disconnect current socket
      socket.disconnect();

      // 4. Clear message history if requested
      if (clearHistory) {
        messageHistory.value = [];
      }

      // 5. Wait for reconnection delay
      await new Promise((resolve) => setTimeout(resolve, reconnectDelay));

      // 6. Reconnect
      socket.connect();

      // 7. Re-setup event listeners
      setupChatEventListeners();

      // 8. Restart intervals
      startHealthCheck();
      startHeartbeat();

      // 9. Rejoin room if preserveRoom is true
      if (preserveRoom && cid) {
        await waitForConnection();
        socket.joinRoom(cid);
      }
    } catch (error) {
      console.error('Failed to refresh socket:', error);
      throw error;
    }
  };

  // Wait for socket connection to be established
  const waitForConnection = () => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000); // 10 second timeout

      const checkConnection = () => {
        if (socket.isConnected.value) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };

      checkConnection();
    });
  };

  // Clean up event listeners
  const cleanupEventListeners = () => {
    Object.entries(eventCallbacks.value).forEach(([event, callback]) => {
      if (callback) {
        socket.off(WebSocketEvents[event.toUpperCase().replace(/([A-Z])/g, '_$1')], callback);
      }
    });
  };

  // Enhanced event listener methods with callback storage
  const onNotification = (callback) => {
    eventCallbacks.value.notification = callback;
    socket.on(WebSocketEvents.NOTIFICATION, callback);
  };

  const onUserStatus = (callback) => {
    eventCallbacks.value.userStatus = callback;
    socket.on(WebSocketEvents.USER_STATUS, callback);
  };

  const onStreamingUpdate = (callback) => {
    eventCallbacks.value.streamingUpdate = callback;
    socket.on(WebSocketEvents.STREAMING_UPDATE, callback);
  };

  const onError = (callback) => {
    eventCallbacks.value.error = callback;
    socket.on(WebSocketEvents.ERROR, callback);
  };

  // Enhanced popChatMessages with safety checks
  const popChatMessages = (length = 10) => {
    if (!Array.isArray(messageHistory.value)) {
      messageHistory.value = [];
      return [];
    }

    const actualLength = Math.min(length, messageHistory.value.length);
    const messages = messageHistory.value.splice(0, actualLength);
    return messages;
  };

  // Initialize socket and monitoring
  if (options.enabled) {
    initializeSocket();

    onBeforeUnmount(() => {
      cleanupEventListeners();
      stopHealthCheck();
      stopHeartbeat();
      socket.disconnect();
    });
  }

  return {
    ...socket,
    messageHistory,

    // Event listeners
    onNotification,
    onUserStatus,
    onStreamingUpdate,
    onError,

    // Message management
    popChatMessages,

    // Socket management
    refreshSocket,
  };
}
